import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { RelationPicker } from './ui';
import { RELATION_CONFIGS, relationLabel } from './relationConfigs';
import { MultiChipSelect } from './campaign/MultiChipSelect';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/criticalskus';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const ACTIVE_OPTIONS = [{ value: '1', label: 'Aktif' }, { value: '0', label: 'Pasif' }];

// DateTimeOffset (ISO) ↔ <input type="date"> (YYYY-MM-DD)
const isoToDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dateInputToIso = (v, endOfDay = false) => {
  if (!v) return null;
  return new Date(`${v}${endOfDay ? 'T23:59:59' : 'T00:00:00'}`).toISOString();
};

const EMPTY = {
  id: '', textId: '', description: '', isActive: '1', startDate: '', endDate: '',
  representativeId: '', customerId: '', customerGroupId: '', customerSubGroupId: '',
  customerDynamicGroupId: '', additionalFieldId: '', additionalFieldValue: '',
};

export function CriticalSkuDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Kritik SKU' : 'Kritik SKU Detayı', 'Kritik ürün tanımı');

  const [form, setForm] = useState(EMPTY);
  const [products, setProducts] = useState([]); // [{ productId, label }]
  const [labels, setLabels] = useState({}); // id -> seçili kriter etiketi
  const [additionalFields, setAdditionalFields] = useState([]);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const labelFor = (key) => (form[key] ? (labels[form[key]] || '') : '');
  const onPick = (key) => (val, item) => {
    set(key, val || '');
    if (val && item) setLabels((prev) => ({ ...prev, [val]: relationLabel(item) }));
  };

  // Özelleştirilmiş saha tanımları
  useEffect(() => {
    fetch('/api/customeradditionalfields?$top=500&$orderby=Name', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setAdditionalFields(getODataRows(d)))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Detay alınamadı');
      const p = body?.data ?? body?.Data ?? {};
      const h = p.header || {};
      setForm({
        id: h.id || '', textId: h.textId || '', description: h.description || '',
        isActive: h.isActive === false ? '0' : '1',
        startDate: isoToDateInput(h.startDate), endDate: isoToDateInput(h.endDate),
        representativeId: h.representativeId || '', customerId: h.customerId || '',
        customerGroupId: h.customerGroupId || '', customerSubGroupId: h.customerSubGroupId || '',
        customerDynamicGroupId: h.customerDynamicGroupId || '',
        additionalFieldId: h.additionalFieldId || '', additionalFieldValue: h.additionalFieldValue || '',
      });
      setLabels({
        [h.representativeId]: p.representativeLabel, [h.customerId]: p.customerLabel,
        [h.customerGroupId]: p.customerGroupLabel, [h.customerSubGroupId]: p.customerSubGroupLabel,
        [h.customerDynamicGroupId]: p.dynamicGroupLabel,
      });
      setProducts((p.products || []).map((d) => ({ productId: d.productId ?? d.ProductId, label: d.label ?? d.Label ?? '' })));
      setAudit({ createdBy: h.createdBy ?? h.CreatedBy ?? h.createdUser ?? h.CreatedUser, createdDate: h.createdDate ?? h.CreatedDate, updatedBy: h.updatedBy ?? h.UpdatedBy ?? h.modifiedUser ?? h.ModifiedUser, updatedDate: h.updatedDate ?? h.UpdatedDate ?? h.modifiedDate ?? h.ModifiedDate });
    } catch (e) {
      showToast('error', e.message || 'Detay alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (products.length === 0) { showToast('error', 'En az bir ürün ekleyin'); return; }
    if (form.startDate && form.endDate && form.startDate > form.endDate) {
      showToast('error', 'Başlangıç tarihi bitiş tarihinden sonra olamaz'); return;
    }
    setSaving(true);
    try {
      const payload = {
        header: {
          id: form.id || EMPTY_GUID,
          // Kod sistem tarafından otomatik atanır — yeni kayıtta boş gönderilir.
          textId: form.textId || null,
          description: form.description,
          startDate: dateInputToIso(form.startDate, false),
          endDate: dateInputToIso(form.endDate, true),
          representativeId: form.representativeId || null,
          customerId: form.customerId || null,
          customerGroupId: form.customerGroupId || null,
          customerSubGroupId: form.customerSubGroupId || null,
          customerDynamicGroupId: form.customerDynamicGroupId || null,
          additionalFieldId: form.additionalFieldId || null,
          additionalFieldValue: form.additionalFieldId ? (form.additionalFieldValue || null) : null,
          isActive: form.isActive === '1',
        },
        productIds: products.map((p) => p.productId),
      };
      const res = await fetch(`${API_BASE}/SaveWithDetails`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kaydedilemedi');
      if (isNew && andNew) {
        showToast('success', 'Kritik SKU eklendi. Yeni kayda devam edebilirsiniz.');
        setForm(EMPTY); setProducts([]); setLabels({}); setTried(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      showToast('success', 'Kritik SKU kaydedildi');
      setTimeout(() => navigate('/critical-skus'), 400);
    } catch (e) {
      showToast('error', e.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: form.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Kritik SKU silindi');
      setTimeout(() => navigate('/critical-skus'), 400);
    } catch (e) {
      showToast('error', e.message || 'Silme sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const additionalFieldOptions = [
    { value: '', label: 'Tümü' },
    ...additionalFields.map((f) => ({ value: f.id, label: f.name || f.Name })),
  ];

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/critical-skus')} disabled={saving}>İptal</button>
      {canSave && (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={() => persist()}
          disabled={saving}
          menu={isNew ? [{
            label: 'Kaydet ve Yeni', desc: 'Kaydet, yeni kayıt aç', icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }] : []}
        />
      )}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Kritik SKU', to: '/critical-skus' }, { label: isNew ? 'Yeni Kritik SKU' : 'Kritik SKU Detayı', current: true }]}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'CriticalSku', entityId: id }}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="info" title="Tanım" subtitle="Kod sistem tarafından otomatik atanır">
          <Grid cols={2}>
            <FieldCustom label="Kod">
              <div className="dps-ff-input">
                <input className="mono" readOnly value={isNew ? '' : form.textId}
                  placeholder={isNew ? 'Kaydedildiğinde otomatik atanacak' : ''} />
              </div>
            </FieldCustom>
            <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
            <FieldText form={form} set={set} k="description" label="Açıklama" span />
          </Grid>
        </Section>

        <Section
          icon="users"
          title="Geçerlilik & Hedefleme"
          subtitle="Bu ürünlerin hangi tarihlerde ve hangi noktalarda kritik sayılacağı"
          note="Boş bırakılan kriter “farketmez” sayılır; dolu kriterlerin tamamını sağlayan noktalar hedeflenir."
        >
          <Grid cols={2}>
            <FieldDate form={form} set={set} k="startDate" label="Tarih Aralığı — Başlangıç" />
            <FieldDate form={form} set={set} k="endDate" label="Tarih Aralığı — Bitiş" />
          </Grid>
          <Grid cols={3}>
            <FieldCustom label="Satış Temsilcisi">
              <RelationPicker config={RELATION_CONFIGS.representative} value={form.representativeId} selectedLabel={labelFor('representativeId')} onChange={onPick('representativeId')} placeholder="Tümü" />
            </FieldCustom>
            <FieldCustom label="Müşteri Grubu">
              <RelationPicker config={RELATION_CONFIGS.customerGroup} value={form.customerGroupId} selectedLabel={labelFor('customerGroupId')} onChange={onPick('customerGroupId')} placeholder="Tümü" />
            </FieldCustom>
            <FieldCustom label="Müşteri Ek Grubu">
              <RelationPicker config={RELATION_CONFIGS.customerSubGroup} value={form.customerSubGroupId} selectedLabel={labelFor('customerSubGroupId')} onChange={onPick('customerSubGroupId')} placeholder="Tümü" />
            </FieldCustom>
            <FieldCustom label="Müşteri Özelleştirilmiş Grup">
              <RelationPicker config={RELATION_CONFIGS.customerDynamicGroup} value={form.customerDynamicGroupId} selectedLabel={labelFor('customerDynamicGroupId')} onChange={onPick('customerDynamicGroupId')} placeholder="Tümü" />
            </FieldCustom>
            <FieldSelect form={form} set={(k, v) => setForm((c) => ({ ...c, additionalFieldId: v, additionalFieldValue: '' }))} k="additionalFieldId" label="Müşteri Özelleştirilmiş Saha" options={additionalFieldOptions} />
            <FieldText form={form} set={set} k="additionalFieldValue" label="Saha Değeri" placeholder={form.additionalFieldId ? 'Beklenen değer' : 'Önce saha seçin'} />
            <FieldCustom label="Tekil Nokta (Müşteri)" span>
              <RelationPicker config={RELATION_CONFIGS.customer} value={form.customerId} selectedLabel={labelFor('customerId')} onChange={onPick('customerId')} placeholder="Tümü" />
            </FieldCustom>
          </Grid>
        </Section>

        <Section
          icon="box"
          title={`Kritik Ürünler (${products.length})`}
          subtitle="Ara ve seç — seçtiğin her ürün anında eklenir. Bu noktalarda en üstte vurgulanır, satılmazsa uyarı verir."
        >
          <FieldCustom label="Ürün ekle (kod/ad ile ara, çoklu seçim)" req invalid={tried && products.length === 0}>
            <MultiChipSelect
              items={products.map((p) => ({ value: p.productId, label: p.label }))}
              onChange={(items) => setProducts(items.map((i) => ({ productId: i.value, label: i.label })))}
              config={RELATION_CONFIGS.product}
              placeholder="Ürün kodu veya adı ile ara ve ekle…"
            />
          </FieldCustom>
          {tried && products.length === 0 && (
            <div className="dps-ff-msg" style={{ marginTop: 4 }}>En az bir ürün ekleyin</div>
          )}
        </Section>
      </div>

      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.textId}”</strong> kritik SKU tanımını ve ürünlerini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default CriticalSkuDetailPage;
